import { createClientLogger, logClientError } from '@/client/logging/client-logger';
import {
    extractSelectionLimitWarning,
    MASSIVE_RESPONSE_WARNING_TOAST_OPTIONS,
} from '@/components/modal-answer-picked/modal-answer-picked-selection-warning';
import { MarketplaceReviewSelectionDetails } from '@/contracts/marketplace/review-records';
import { isRequestCanceled } from '@/client/error-handling/is-request-canceled';
import { useEffect, useState } from 'react';
import { toast } from '@/ui/toast';
import { TOAST_IDS } from '@/ui/toast-ids';
import { useMarketplaceApi } from '@/context/marketplace.context';

type UseModalAnswerPickedSelectionDataArgs = {
    isShown: boolean;
    accessToken: string;
    selectionSessionId?: string;
    pickedReviewsCount: number;
};

const modalAnswerPickedLogger = createClientLogger({
    scope: 'modal-answer-picked',
});

export const useModalAnswerPickedSelectionData = ({
    isShown,
    accessToken,
    selectionSessionId,
    pickedReviewsCount,
}: UseModalAnswerPickedSelectionDataArgs) => {
    const { reviewsApi } = useMarketplaceApi();

    const [selectedReviewsForAnswer, setSelectedReviewsForAnswer] = useState<MarketplaceReviewSelectionDetails[]>([]);
    const [isSelectedReviewsLoading, setIsSelectedReviewsLoading] = useState(false);
    const [selectedReviewsErrorText, setSelectedReviewsErrorText] = useState<string | null>(null);

    useEffect(() => {
        if (!isShown) {
            return;
        }

        if (!selectionSessionId || !pickedReviewsCount) {
            setSelectedReviewsForAnswer([]);
            setSelectedReviewsErrorText(null);
            setIsSelectedReviewsLoading(false);
            return;
        }

        const abortController = new AbortController();

        setIsSelectedReviewsLoading(true);
        setSelectedReviewsErrorText(null);

        void reviewsApi
            .getSelectedReviewsForAnswer(selectionSessionId, {
                authToken: accessToken,
                signal: abortController.signal,
            })
            .then((response) => {
                const limitWarning = extractSelectionLimitWarning(response.headers as Record<string, unknown>);
                if (limitWarning) {
                    toast.warning(limitWarning, {
                        ...MASSIVE_RESPONSE_WARNING_TOAST_OPTIONS,
                        toastId: TOAST_IDS.answerPickLoadSelectionLimitWarning,
                    });
                }
                setSelectedReviewsForAnswer(response.data);
            })
            .catch((error: unknown) => {
                if (isRequestCanceled(error)) {
                    return;
                }

                logClientError({
                    logger: modalAnswerPickedLogger,
                    message:
                        '[client.review-selection.selected-reviews-load] Failed to load selected reviews for mass answer modal.',
                    error,
                    context: {
                        selectionSessionId,
                        pickedReviewsCount,
                    },
                });
                setSelectedReviewsErrorText('Не удалось загрузить выбранные отзывы');
                setSelectedReviewsForAnswer([]);
            })
            .finally(() => {
                if (!abortController.signal.aborted) {
                    setIsSelectedReviewsLoading(false);
                }
            });

        return () => {
            abortController.abort();
        };
    }, [accessToken, isShown, pickedReviewsCount, selectionSessionId]);

    return {
        selectedReviewsForAnswer,
        setSelectedReviewsForAnswer,
        isSelectedReviewsLoading,
        selectedReviewsErrorText,
    };
};
